import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useEffect, useMemo, type ReactElement } from 'react';
import { Alert, DatePicker, Form, Input, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import ruRU from 'antd/es/date-picker/locale/ru_RU.js';
import enUS from 'antd/es/date-picker/locale/en_US.js';

import { EntityLinkFields } from '../../entity-links/EntityLinkFields.js';
import { useEntityLinkCascade } from '../../entity-links/useEntityLinkCascade.js';
import type { CreateTaskInput, TaskItem, TaskPriority, TaskStatus } from '../api/tasks.client.js';
import { useMembers } from '../hooks/useTaskLookups.js';
import { useCreateTaskMutation, useUpdateTaskMutation } from '../hooks/useTaskMutations.js';
import {
  isoToLocalInput,
  localInputToIso,
  nowLocalInput,
  getTaskPriorityOptions,
  getTaskStatusOptions,
} from '../lib/taskMeta.js';

interface TaskFormValues {
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeMemberId?: string | undefined;
  dealId?: string | undefined;
  companyId?: string | undefined;
  contactId?: string | undefined;
  dueLocal?: string | undefined;
}

interface TaskFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Передана — режим редактирования; иначе создание. */
  task?: TaskItem | null;
  initialStatus?: TaskStatus;
  initialDueAt?: string;
  /** Карточка сущности: жёстко привязать и скрыть соответствующее поле. */
  lockedDealId?: string;
  lockedContactId?: string;
  lockedCompanyId?: string;
  /** Задача, заведённая с карточки документа, остаётся его задачей. */
  lockedDocumentId?: string;
  /** Задача с карточки проекта остаётся задачей проекта. */
  lockedProjectId?: string;
}

export function TaskFormModal({
  open,
  onClose,
  task,
  initialStatus,
  initialDueAt,
  lockedDealId,
  lockedContactId,
  lockedCompanyId,
  lockedDocumentId,
  lockedProjectId,
}: TaskFormModalProps): ReactElement {
  useI18nSubscription();
  const [form] = Form.useForm<TaskFormValues>();
  const isEdit = Boolean(task);

  const membersQuery = useMembers();
  // Каскад «компания → контакт → сделка» общий с формой встречи.
  const cascade = useEntityLinkCascade(form);

  const createMut = useCreateTaskMutation();
  const updateMut = useUpdateTaskMutation();
  const pending = createMut.isPending || updateMut.isPending;
  const error = (createMut.error ?? updateMut.error) as Error | null;

  const memberOptions = useMemo(
    () => (membersQuery.data ?? []).map((m) => ({ value: m.id, label: m.name || m.email })),
    [membersQuery.data],
  );

  // Срок нельзя ставить в прошлое. Исключение — уже сохранённый срок старой задачи
  // или явно заданная начальная дата (например, из календаря).
  const existingDueLocal = isoToLocalInput(task?.dueAt);
  const presetDueLocal = initialDueAt ? isoToLocalInput(initialDueAt) : undefined;
  const currentLocal = nowLocalInput();
  const minDueLocal =
    existingDueLocal && existingDueLocal < currentLocal
      ? existingDueLocal
      : presetDueLocal && presetDueLocal < currentLocal
        ? presetDueLocal
        : currentLocal;
  useEffect(() => {
    if (!open) return;
    createMut.reset();
    updateMut.reset();
    if (task) {
      form.setFieldsValue({
        title: task.title,
        description: task.description ?? '',
        status: task.status,
        priority: task.priority,
        assigneeMemberId: task.assigneeMember?.id ?? undefined,
        dealId: task.deal?.id ?? undefined,
        companyId: task.company?.id ?? undefined,
        contactId: task.contact?.id ?? undefined,
        dueLocal: isoToLocalInput(task.dueAt),
      });
    } else {
      form.setFieldsValue({
        title: '',
        description: '',
        status: initialStatus ?? 'todo',
        priority: 'medium',
        assigneeMemberId: undefined,
        dealId: lockedDealId ?? undefined,
        companyId: lockedCompanyId ?? undefined,
        contactId: lockedContactId ?? undefined,
        dueLocal: presetDueLocal ?? '',
      });
    }
  }, [
    open,
    task,
    initialStatus,
    initialDueAt,
    presetDueLocal,
    lockedDealId,
    lockedContactId,
    lockedCompanyId,
    form,
  ]);

  const handleSubmit = (values: TaskFormValues): void => {
    const dueIso = localInputToIso(values.dueLocal);
    if (isEdit && task) {
      // В режиме правки undefined из allowClear трактуем как «очистить» → null.
      updateMut.mutate(
        {
          id: task.id,
          input: {
            title: values.title,
            description: values.description?.trim() ? values.description : null,
            status: values.status,
            priority: values.priority,
            assigneeMemberId: values.assigneeMemberId ?? null,
            dealId: lockedDealId ?? values.dealId ?? null,
            companyId: lockedCompanyId ?? values.companyId ?? null,
            contactId: lockedContactId ?? values.contactId ?? null,
            ...(lockedDocumentId ? { documentId: lockedDocumentId } : {}),
            ...(lockedProjectId ? { projectId: lockedProjectId } : {}),
            dueAt: dueIso ?? null,
            expectedUpdatedAt: task.updatedAt,
          },
        },
        { onSuccess: () => onClose() },
      );
      return;
    }
    const dealId = lockedDealId ?? values.dealId;
    const companyId = lockedCompanyId ?? values.companyId;
    const contactId = lockedContactId ?? values.contactId;
    const input: CreateTaskInput = {
      title: values.title,
      status: values.status,
      priority: values.priority,
      ...(values.description?.trim() ? { description: values.description } : {}),
      ...(values.assigneeMemberId ? { assigneeMemberId: values.assigneeMemberId } : {}),
      ...(dealId ? { dealId } : {}),
      ...(companyId ? { companyId } : {}),
      ...(contactId ? { contactId } : {}),
      ...(lockedDocumentId ? { documentId: lockedDocumentId } : {}),
      ...(lockedProjectId ? { projectId: lockedProjectId } : {}),
      ...(dueIso ? { dueAt: dueIso } : {}),
    };
    createMut.mutate(input, { onSuccess: () => onClose() });
  };

  return (
    <Modal
      closable={{ 'aria-label': i18n.t('crm.sidebar.close') }}
      title={
        isEdit
          ? i18n.t('crm.features.features.tasks.components.taskformmodal.редактировать_задачу')
          : i18n.t('crm.features.features.tasks.components.taskformmodal.новая_задача')
      }
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={
        isEdit
          ? i18n.t('crm.features.features.tasks.components.taskformmodal.сохранить')
          : i18n.t('crm.features.features.tasks.components.taskformmodal.создать')
      }
      cancelText={i18n.t('crm.features.features.tasks.components.taskformmodal.отмена')}
      confirmLoading={pending}
      width={560}
      destroyOnHidden
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={i18n.t(
            'crm.features.features.tasks.components.taskformmodal.не_удалось_сохранить_задачу',
          )}
          description={error.message}
        />
      ) : null}
      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={pending}>
        <Form.Item
          name="title"
          label={i18n.t('crm.features.features.tasks.components.taskformmodal.название')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: i18n.t(
                'crm.features.features.tasks.components.taskformmodal.введите_название_задачи',
              ),
            },
          ]}
        >
          <Input
            placeholder={i18n.t(
              'crm.features.features.tasks.components.taskformmodal.что_нужно_сделать',
            )}
            maxLength={200}
            autoFocus
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={i18n.t('crm.features.features.tasks.components.taskformmodal.описание')}
        >
          <Input.TextArea
            placeholder={i18n.t(
              'crm.features.features.tasks.components.taskformmodal.детали_задачи_необязательно',
            )}
            rows={3}
            maxLength={10_000}
          />
        </Form.Item>

        <div className="flex gap-3">
          <Form.Item
            name="status"
            label={i18n.t('crm.features.features.tasks.components.taskformmodal.статус')}
            className="flex-1"
          >
            <Select options={getTaskStatusOptions()} />
          </Form.Item>
          <Form.Item
            name="priority"
            label={i18n.t('crm.features.features.tasks.components.taskformmodal.приоритет')}
            className="flex-1"
          >
            <Select options={getTaskPriorityOptions()} />
          </Form.Item>
        </div>

        <Form.Item
          name="dueLocal"
          getValueProps={(value: string | undefined) => ({ value: value ? dayjs(value) : null })}
          normalize={(value: Dayjs | null) => value?.format('YYYY-MM-DDTHH:mm') ?? ''}
          label={i18n.t('crm.features.features.tasks.components.taskformmodal.срок')}
          rules={[
            {
              validator: (_rule, value: string | undefined) => {
                // Новый срок в прошлом не даём поставить; уже сохранённый (у старой
                // задачи) не считаем ошибкой, иначе её нельзя было бы отредактировать.
                if (!value || value === existingDueLocal) return Promise.resolve();
                return new Date(value).getTime() < Date.now()
                  ? Promise.reject(
                      new Error(
                        i18n.t(
                          'crm.features.features.tasks.components.taskformmodal.срок_не_может_быть_в_прошлом',
                        ),
                      ),
                    )
                  : Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            showTime
            format="DD.MM.YYYY HH:mm"
            locale={i18n.locale === 'ru' ? ruRU : enUS}
            minDate={dayjs(minDueLocal).startOf('day')}
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="assigneeMemberId"
          label={i18n.t('crm.features.features.tasks.components.taskformmodal.исполнитель')}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={i18n.t('crm.features.features.tasks.components.taskformmodal.не_назначен')}
            loading={membersQuery.isLoading}
            options={memberOptions}
          />
        </Form.Item>

        <EntityLinkFields
          cascade={cascade}
          {...(lockedDealId ? { lockedDealId } : {})}
          {...(lockedContactId ? { lockedContactId } : {})}
          {...(lockedCompanyId ? { lockedCompanyId } : {})}
        />
      </Form>
    </Modal>
  );
}
